import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import { Typography } from "@mui/material";
import { useStyles } from "./DowntimeCounter.styles";
import { MachineCenterContext } from "../../contexts/MachineCenterContext/MachineCenterContext";
import {
    setCurrentStartDowntime,
    getCurrentStartDowntime,
    removeCurrentStartDowntime,
    getCurrentStartBelowTriggerDowntimeCount,
    setNotSelectedOrBelowTriggerEventReportId,
    setCurrentStartBelowTriggerDowntimeCount,
} from "../../utilities/storageUtilities";
import { MachineCentersEntity } from "entities/MachineCenters/MachineCentersEntity";
import { HMICachedDataContext } from "contexts/HMICachedDataContext/HMICachedDataContext";
import moment from "moment";
import { FAILURE_TYPES_CODE_EXCEPTIONS, MEASURE_METRICS } from "constants/constants";
import { formatCounter } from "utilities/utilities";

export const DowntimeCounter = () => {
    const { store } = useContext(HMICachedDataContext);
    const currentStartDowntime = getCurrentStartDowntime();
    const currentStartDowntimeBelowTrigger = getCurrentStartBelowTriggerDowntimeCount();
    const { t } = useTranslation();
    const classes = useStyles();
    const initTime = currentStartDowntime
        ? +new Date() - currentStartDowntime
        : currentStartDowntimeBelowTrigger
        ? +new Date() - currentStartDowntimeBelowTrigger
        : 0;
    const [time, setTime] = useState(initTime);
    const { downtimeCounter, downtimeCounterBelowTrigger } = useContext(MachineCenterContext);

    useEffect(() => {
        let interval: ReturnType<typeof setTimeout> | null = null;

        async function getCurrentMachineCenterStatus() {
            try {
                const currentMachineStatus = await MachineCentersEntity.getMachineCenterCurrentStatus([
                    store.machineCenter?.machine_center_id!,
                ]);

                if (
                    currentMachineStatus &&
                    currentMachineStatus[0] &&
                    currentMachineStatus[0].measure_metric_id === MEASURE_METRICS.DOWNTIME
                ) {
                    const dateCounter = new Date(
                        moment(
                            (currentMachineStatus[0].start_date.toString() as string) + "-00:00",
                            "YYYY/MM/DD HH:mm:ss ZZ",
                        )
                            .local()
                            .toString(),
                    ).getTime();
                    const isBelowTrigger =
                        currentMachineStatus[0]?.failure_type?.code === FAILURE_TYPES_CODE_EXCEPTIONS.BELOW_TRIGGER;
                    const isNotSelected =
                        currentMachineStatus[0]?.failure_type?.code === FAILURE_TYPES_CODE_EXCEPTIONS.NOT_SELECTED;

                    if (isBelowTrigger || isNotSelected) {
                        setNotSelectedOrBelowTriggerEventReportId(currentMachineStatus[0].event_report_id);
                        isBelowTrigger && setCurrentStartBelowTriggerDowntimeCount(dateCounter);
                    }
                    !isBelowTrigger && setCurrentStartDowntime(dateCounter);
                }
            } catch (error) {
                console.error("Error fetching machine center current status:", error);
            }
        }

        getCurrentMachineCenterStatus();

        let startTime: number = currentStartDowntime
            ? currentStartDowntime
            : currentStartDowntimeBelowTrigger
            ? currentStartDowntimeBelowTrigger
            : +new Date();
        setCurrentStartDowntime(startTime);

        if (downtimeCounter || downtimeCounterBelowTrigger) {
            interval = setInterval(() => {
                let currentTime: Date = new Date();
                setTime(+currentTime - startTime);
            }, 10);
        } else {
            clearInterval(interval!);
            removeCurrentStartDowntime();
        }

        return () => {
            clearInterval(interval!);
            removeCurrentStartDowntime();
        };
    }, [
        downtimeCounter,
        currentStartDowntime,
        currentStartDowntimeBelowTrigger,
        downtimeCounterBelowTrigger,
        store.machineCenter?.machine_center_id,
    ]);

    return (
        <Box className={classes.counterContainer}>
            <Typography variant="subtitle2" className={classes.counterTitleTypography}>
                <p className={classes.counterTitle}>{t("HMI.current_downtime").toUpperCase()}</p>
            </Typography>
            <Box className={classes.counterClock}>
                <Typography variant="h2">
                    <span>{formatCounter(time)}</span>
                </Typography>
            </Box>
        </Box>
    );
};
